// Graph "screenshot" exports. PNG merges sigma's live canvas layers (exact pixels,
// including WebGL glow and label placement); SVG re-draws the current viewport as
// vector shapes from the post-reducer display data, so spotlight dimming and hidden
// cross-cluster edges are respected.
import Sigma from 'sigma';
import { BACKGROUND_RGB, LABEL_TEXT_COLOR } from './graphColors';
import { EdgeAttributes, NodeAttributes } from './graphTypes';

type GraphSigma = Sigma<NodeAttributes, EdgeAttributes>;

const BACKGROUND_COLOR = `rgb(${BACKGROUND_RGB.join(', ')})`;

function triggerDownload(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export function exportGraphAsPng(sigma: GraphSigma, fileName: string): void {
  const { width, height } = sigma.getDimensions();
  const pixelRatio = window.devicePixelRatio || 1;
  const output = document.createElement('canvas');
  output.width = width * pixelRatio;
  output.height = height * pixelRatio;
  const ctx = output.getContext('2d');
  if (!ctx) return;

  ctx.fillStyle = BACKGROUND_COLOR;
  ctx.fillRect(0, 0, output.width, output.height);

  // sigma's WebGL contexts don't preserve the drawing buffer, so the layers must
  // be read in the same task as a render — force one synchronously first
  sigma.refresh();
  Object.entries(sigma.getCanvases()).forEach(([layer, canvas]) => {
    if (layer === 'mouse') return;
    ctx.drawImage(canvas, 0, 0, output.width, output.height);
  });

  output.toBlob((blob) => {
    if (blob) triggerDownload(blob, fileName);
  }, 'image/png');
}

// 8-digit hex (our premultiplied colors carry an alpha suffix) → rgba(), which
// older SVG viewers understand; everything else passes through unchanged
function svgColor(color: string): string {
  if (color.startsWith('#') && color.length === 9) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    const a = parseInt(color.slice(7, 9), 16) / 255;
    return `rgba(${r}, ${g}, ${b}, ${a.toFixed(3)})`;
  }
  return color;
}

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function exportGraphAsSvg(sigma: GraphSigma, fileName: string): void {
  const graph = sigma.getGraph();
  const { width, height } = sigma.getDimensions();

  const edges: string[] = [];
  graph.forEachEdge((edge, attrs, source, target) => {
    const display = sigma.getEdgeDisplayData(edge);
    if (!display || display.hidden) return;
    const sourceDisplay = sigma.getNodeDisplayData(source);
    const targetDisplay = sigma.getNodeDisplayData(target);
    if (!sourceDisplay || !targetDisplay || sourceDisplay.hidden || targetDisplay.hidden) return;

    const from = sigma.framedGraphToViewport(sourceDisplay);
    const to = sigma.framedGraphToViewport(targetDisplay);
    const strokeWidth = Math.max(sigma.scaleSize(display.size), 0.3);
    // approximate @sigma/edge-curve: a quadratic bezier whose control point sits on
    // the segment's perpendicular, offset proportionally to length × curvature
    const controlX = (from.x + to.x) / 2 + (to.y - from.y) * attrs.curvature * 0.5;
    const controlY = (from.y + to.y) / 2 - (to.x - from.x) * attrs.curvature * 0.5;
    edges.push(
      `<path d="M ${from.x.toFixed(1)} ${from.y.toFixed(1)} Q ${controlX.toFixed(1)} ${controlY.toFixed(1)} ${to.x.toFixed(1)} ${to.y.toFixed(1)}" fill="none" stroke="${svgColor(display.color)}" stroke-width="${strokeWidth.toFixed(2)}" stroke-linecap="round"/>`,
    );
  });

  const nodes: string[] = [];
  const labels: string[] = [];
  graph.forEachNode((node, attrs) => {
    const display = sigma.getNodeDisplayData(node);
    if (!display || display.hidden) return;
    const pos = sigma.framedGraphToViewport(display);
    const radius = Math.max(sigma.scaleSize(display.size), 0.5);
    nodes.push(
      `<circle cx="${pos.x.toFixed(1)}" cy="${pos.y.toFixed(1)}" r="${radius.toFixed(2)}" fill="${svgColor(display.color)}"/>`,
    );
    // persistent (constellation) labels only — hover-grid labels are viewport noise
    if (attrs.forceLabel && display.label) {
      labels.push(
        `<text x="${(pos.x + radius + 4).toFixed(1)}" y="${(pos.y + 4).toFixed(1)}">${escapeXml(display.label)}</text>`,
      );
    }
  });

  const svg = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
    `<rect width="100%" height="100%" fill="${BACKGROUND_COLOR}"/>`,
    `<g>${edges.join('')}</g>`,
    `<g>${nodes.join('')}</g>`,
    `<g fill="${LABEL_TEXT_COLOR}" font-family="system-ui, sans-serif" font-size="11">${labels.join('')}</g>`,
    '</svg>',
  ].join('\n');

  triggerDownload(new Blob([svg], { type: 'image/svg+xml' }), fileName);
}
